<script setup lang="ts">

import {computed} from 'vue';
import {
  type InterventionPreview,
  PublicationState,
} from '@/ts/types/dto/interventions-dto';
import {pagesPath} from '@/ts/router/pages-path';
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import {getVueLink} from '@/ts/composables/stateful/use-route-params';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import InterventionLabel from '@/vue/molecules/intervention-label.vue';
import InterventionFooter from '@/vue/molecules/intervention-footer.vue';
import {useRouter} from 'vue-router';

const props = defineProps<{ intervention: InterventionPreview }>();
const router = useRouter();
const intervention = computed<InterventionPreview>(() => props.intervention);
const interventionState = computed(() => {
  const stateMap: Record<PublicationState, string> = {
    [PublicationState.IN_PROGRESS]: 'in-progress',
    [PublicationState.WITHDREW]: 'withdrew',
    [PublicationState.COMPLETED]: 'completed',
    [PublicationState.NOT_JOINED]: 'not-joined',
    [PublicationState.ACTIVE]: 'active',
    [PublicationState.NEW]: 'new',
    [PublicationState.MAINTENANCE]: 'maintenance',
    [PublicationState.DISCONTINUED]: 'discontinued',
    [PublicationState.PREVIEW]: 'preview',
    [PublicationState.PUBLISHED]: 'published',
  };
  return stateMap[intervention.value.publicationState] ?? 'not-started';
});

const firstCitation = computed(() => {
  const citations = intervention.value.indication?.citations;
  return citations && citations.length > 0 ? citations[0] : null;
});

const recapLink = computed(() => getVueLink(pagesPath.intervention.interventionRecap, {iid: intervention.value.id}));
const cardLink = computed(() => getVueLink(pagesPath.intervention.interventionLanding, {iid: intervention.value.id}));

function navigateToIntervention(event: MouseEvent | KeyboardEvent): void {
  if (event.target instanceof Element) {
    const isRecapLink = event.target.closest('.recap-link');
    const isCitationLink = event.target.closest('.citation-link');
    if (isRecapLink ?? isCitationLink) {
      return;
    }
  }
  void router.push(cardLink.value);
}
</script>

<template>
  <div
    class="intervention"
    :class="interventionState"
    :aria-label="'Navigate to details for ' + intervention.name"
    tabindex="0"
    role="link"
    @click="navigateToIntervention"
    @keydown.enter.prevent="navigateToIntervention"
  >
    <div class="top-row">
      <lazy-image :src="intervention.image!" :width="367" class="img"/>
      <intervention-label class="label" :publication-state="intervention.publicationState"/>
    </div>
    <div class="section">
      <h2 class="title">
        {{ intervention.name }}
      </h2>
      <span class="description">{{ intervention.description }}</span>
      <div v-if="firstCitation" class="indication-citation" @click.stop>
        <a
          :href="firstCitation.reference"
          target="_blank"
          rel="noopener noreferrer"
          class="citation-link"
          @click.stop
        >
          {{ firstCitation.name }}
        </a>
      </div>
      <router-link v-if="intervention.publicationState === PublicationState.COMPLETED" class="recap-link" :to="recapLink">
        <span>{{ $t('intervention_recap') }}</span>
      </router-link>
      <div class="bottom-block">
        <completed-progress
          v-if="PublicationState.IN_PROGRESS === intervention.publicationState"
          :completed="intervention.completionProgress ?? 0"
          class="progress"
        />
        <intervention-footer :intervention="intervention"/>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.progress {
  margin: var(--Spacing-spacing-3) 0;
}

.bottom-block {
  margin-top: auto;
  display: flex;
  flex-direction: column;
}

.intervention {
  @include q-card-20;
  width: 100%;
  flex-direction: column;
  max-height: 800px;
  display: flex;
  border-radius: var(--card-border-radius);
  cursor: pointer;
  text-decoration: none;
  color: inherit;

  @include screen-md {
    min-height: 520px;
  }

  @include screen-lg {
    min-height: 670px;
  }

  &.active, &.withdrew, &.published, &.new {
    background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-20);
    color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  }

  &.in-progress {
    @include q-card-light-current;
  }

  &.completed {
    @include q-card-light-completed;
  }

  &.maintenance {
    @include q-card-red;
  }

  &.preview {
    @include q-card-30;
  }
}

.title {
  @include font-display-5;
}

.description {
  @include font-body-sm;
  margin: var(--Spacing-spacing-3) 0;

  @include screen-lg {
    @include font-body-sm;
  }
}

.indication-citation {
  @include font-body-sm;
  margin: var(--Spacing-spacing-3) 0;
  padding-left: var(--Spacing-spacing-4);
  border-left: 4px solid var(--Primary-primary-color-1);
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: scale(1.02);
    filter: brightness(1.1);

    .citation-link {
      text-decoration: underline;
    }
  }
}

.citation-link {
  color: var(--Functional-wl-current-text);
  text-decoration: none;
  cursor: pointer;
  display: inline-block;

  &:visited {
    color: var(--Functional-wl-current-text);
  }
}

.section {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: var(--Spacing-spacing-4);
  font-size: 16px;

  @include screen-lg {
    padding: var(--Spacing-spacing-6);
  }
}

.label {
  position: absolute;
  right: var(--Spacing-spacing-4);
  top: var(--Spacing-spacing-4);

  @include screen-md {
    right: var(--Spacing-spacing-6);
    top: var(--Spacing-spacing-5);
  }
}

.top-row {
  position: relative;
  display: flex;
  justify-content: space-between;
  padding-top: var(--Spacing-spacing-6);
}

.img {
  
  max-height: 200px;
  border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;

  & :deep(.q-img__image) {
    object-fit: contain !important;
  }
}

.recap-link {
  text-decoration: underline;
  cursor: pointer;
  padding-top: var(--Spacing-spacing-3);
  padding-bottom: var(--Spacing-spacing-3);
}
</style>
